import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, Megaphone, Car, Tag } from 'lucide-react';
import {
  getNotifications, getUnreadCount, markAllRead, markOneRead, broadcastNotification, getNotificationDetails,
} from '../api/notifications';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Drawer from '../components/ui/Drawer';
import Input from '../components/ui/Input';
import Badge from '../components/ui/Badge';
import Pagination from '../components/ui/Pagination';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../context/ToastContext';
import { formatDate, formatCurrency, getStatusColor, getErrorMessage } from '../lib/utils';
import clsx from 'clsx';

const EMPTY_BROADCAST = { title: '', body: '', target: 'all_customers', send_email: false };

function BroadcastForm({ onSubmit, loading, onClose }) {
  const [form, setForm] = useState(EMPTY_BROADCAST);
  const [errors, setErrors] = useState({});

  function set(key, val) {
    setForm((f) => ({ ...f, [key]: val }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: '' }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.body.trim()) errs.body = 'Message is required';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    onSubmit({ title: form.title.trim(), body: form.body.trim(), target: form.target, send_email: form.send_email });
  }

  const targets = [
    { value: 'all_customers', label: 'All customers' },
    { value: 'all_drivers', label: 'All drivers' },
    { value: 'all', label: 'Everyone' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input label="Title" required value={form.title} onChange={(e) => set('title', e.target.value)} error={errors.title} placeholder="Weekend Offer!" />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Message<span className="text-red-500 ml-0.5">*</span></label>
        <textarea
          value={form.body}
          onChange={(e) => set('body', e.target.value)}
          rows={3}
          placeholder="Get 20% off your next ride this weekend."
          className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
        {errors.body && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{errors.body}</p>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Send to</label>
        <div className="flex gap-2">
          {targets.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => set('target', t.value)}
              className={clsx(
                'flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-colors',
                form.target === t.value
                  ? 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                  : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="broadcast_send_email"
          checked={form.send_email}
          onChange={(e) => set('send_email', e.target.checked)}
          className="rounded border-gray-300 dark:border-gray-600 text-amber-600 focus:ring-amber-500"
        />
        <label htmlFor="broadcast_send_email" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Also send as email (to recipients with an email on file)
        </label>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" loading={loading}>Send</Button>
      </div>
    </form>
  );
}

function DetailRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between items-start gap-4 py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <dt className="text-sm text-gray-500 dark:text-gray-400 shrink-0">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 dark:text-gray-100 text-right">{value}</dd>
    </div>
  );
}

function NotificationDetail({ notificationId }) {
  const { data, isLoading } = useQuery({
    queryKey: ['notification-details', notificationId],
    queryFn: () => getNotificationDetails(notificationId).then((r) => r.data),
    enabled: !!notificationId,
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (!data) return null;

  const { notification: n, ride, offer } = data;

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1.5">
          <Badge variant={n.type === 'safety' ? 'danger' : 'default'}>{n.type}</Badge>
          <span className="text-xs text-gray-400">{formatDate(n.created_at)}</span>
        </div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{n.title}</h3>
        {n.body && <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{n.body}</p>}
      </div>

      {ride && (
        <div>
          <div className="flex items-center gap-2 mb-2.5">
            <Car size={15} className="text-amber-600 dark:text-amber-400" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Ride</h4>
            <Badge variant={getStatusColor(ride.status)}>{ride.status}</Badge>
          </div>
          <dl>
            <DetailRow label="Pickup" value={ride.pickup_address} />
            <DetailRow label="Dropoff" value={ride.dropoff_address} />
            <DetailRow label="Vehicle Type" value={ride.vehicle_type} />
            <DetailRow label="Distance" value={ride.distance_km != null ? `${ride.distance_km} km` : null} />
            <DetailRow label="Fare" value={ride.final_fare != null ? formatCurrency(ride.final_fare) : (ride.estimated_fare != null ? `${formatCurrency(ride.estimated_fare)} (est.)` : null)} />
            {ride.driver && (
              <>
                <DetailRow label="Driver" value={ride.driver.name} />
                <DetailRow label="Driver Phone" value={ride.driver.phone} />
                <DetailRow label="Plate" value={ride.driver.plate_number} />
              </>
            )}
          </dl>
        </div>
      )}

      {offer && (
        <div>
          <div className="flex items-center gap-2 mb-2.5">
            <Tag size={15} className="text-amber-600 dark:text-amber-400" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Offer</h4>
            <Badge variant={offer.is_active ? 'success' : 'default'}>{offer.is_active ? 'Active' : 'Inactive'}</Badge>
          </div>
          <dl>
            <DetailRow label="Code" value={offer.code} />
            <DetailRow label="Title" value={offer.title} />
            <DetailRow label="Subtitle" value={offer.subtitle} />
            <DetailRow
              label="Discount"
              value={offer.discount_type === 'percentage' ? `${offer.discount_value}%` : formatCurrency(offer.discount_value)}
            />
            <DetailRow label="Min Ride Value" value={offer.min_ride_value ? formatCurrency(offer.min_ride_value) : null} />
            <DetailRow label="Expires" value={offer.expiry_date ? formatDate(offer.expiry_date) : null} />
          </dl>
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 25;

export default function Notifications() {
  const qc = useQueryClient();
  const { addToast } = useToast();
  const [composeOpen, setComposeOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['notifications-page', page],
    queryFn: () =>
      getNotifications({ unread_only: false, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }).then((r) => ({
        items: r.data,
        total: Number(r.headers['x-total-count'] ?? r.data.length),
      })),
    refetchInterval: 15000,
  });

  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => getUnreadCount().then((r) => r.data),
    refetchInterval: 15000,
  });

  const markAll = useMutation({
    mutationFn: markAllRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications-page'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const markOne = useMutation({
    mutationFn: (id) => markOneRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications-page'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
  });

  const broadcast = useMutation({
    mutationFn: broadcastNotification,
    onSuccess: () => {
      addToast('Notification queued for delivery', 'success');
      setComposeOpen(false);
    },
    onError: (err) => addToast(getErrorMessage(err, 'Failed to send notification'), 'error'),
  });

  const notifications = data?.items ?? [];
  const total = data?.total ?? 0;
  const unread = countData?.unread_count ?? 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Notifications</h2>
          {unread > 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{unread} unread</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <Button
              variant="secondary"
              size="sm"
              icon={CheckCheck}
              loading={markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              Mark all read
            </Button>
          )}
          <Button size="sm" icon={Megaphone} onClick={() => setComposeOpen(true)}>
            Send Notification
          </Button>
        </div>
      </div>

      <Modal isOpen={composeOpen} onClose={() => setComposeOpen(false)} title="Send Notification" size="md">
        <BroadcastForm
          loading={broadcast.isPending}
          onClose={() => setComposeOpen(false)}
          onSubmit={(data) => broadcast.mutate(data)}
        />
      </Modal>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="px-5 py-4 flex items-start gap-3">
              <Skeleton className="h-9 w-9 rounded-full shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
            <Bell size={36} strokeWidth={1.5} />
            <p className="text-sm font-medium">You're all caught up</p>
          </div>
        ) : (
          notifications.map((n) => (
            <div
              key={n.id}
              onClick={() => {
                setSelectedId(n.id);
                if (!n.is_read) markOne.mutate(n.id);
              }}
              className={clsx(
                'flex items-center gap-3 px-5 py-2.5 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50',
                !n.is_read && 'bg-amber-50/60 dark:bg-amber-900/10'
              )}
            >
              <div className={clsx('h-1.5 w-1.5 rounded-full shrink-0', !n.is_read && 'bg-amber-500')} />
              <Bell
                size={15}
                className={clsx('shrink-0', n.is_read ? 'text-gray-400' : 'text-amber-600 dark:text-amber-400')}
              />
              <p className="flex-1 min-w-0 truncate text-sm">
                <span className={clsx(n.is_read ? 'text-gray-700 dark:text-gray-300' : 'font-semibold text-gray-900 dark:text-gray-100')}>
                  {n.title}
                </span>
                {n.body && <span className="text-gray-400 dark:text-gray-500"> — {n.body}</span>}
              </p>
              <span className="text-xs text-gray-400 shrink-0 whitespace-nowrap">{formatDate(n.created_at)}</span>
            </div>
          ))
        )}
      </div>

      {!isLoading && notifications.length > 0 && (
        <Pagination page={page} limit={PAGE_SIZE} total={total} onPageChange={setPage} />
      )}

      <Drawer isOpen={!!selectedId} onClose={() => setSelectedId(null)} title="Notification">
        {selectedId && <NotificationDetail notificationId={selectedId} />}
      </Drawer>
    </div>
  );
}
